import { App, type URLOpenListenerEvent } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

import { logWarning } from '@/lib/log-error';
import { router } from '@/router';

const APP_HOSTS = new Set(['app.borrah.com', 'app.borradh.io']);

/**
 * Links generated before the location-scoped dashboard was retired are still
 * stored in native launch intents, notification payloads, and users' browser
 * history. Map the former location home to its current, organization-scoped
 * equivalent before it reaches TanStack Router.
 */
export function normalizeAppPath(path: string): string {
  if (/^\/dashboard\/l\/[^/]+\/home\/?$/.test(path)) {
    return '/dashboard/home';
  }
  return path;
}

async function navigateFromUrl(rawUrl: string): Promise<void> {
  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return;
  }

  if (parsed.protocol === 'https:' && !APP_HOSTS.has(parsed.host)) {
    return;
  }

  const target = `${normalizeAppPath(parsed.pathname || '/')}${parsed.search}${parsed.hash}`;
  try {
    await router.navigate({ to: target });
  } catch (error) {
    // A native launch intent must not be able to turn an obsolete or malformed
    // path into an unhandled WebView rejection. The app remains usable, while
    // the warning preserves the exact target for follow-up diagnosis.
    logWarning('deepLink.navigate', 'Could not open an app deep link.', {
      feature: 'navigation',
      extra: {
        target,
        reason: error instanceof Error ? error.message : String(error),
      },
    });
  }
}

let installed = false;

export function installDeepLinkListener() {
  if (installed) return;
  if (!Capacitor.isNativePlatform()) return;
  installed = true;

  void App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
    void navigateFromUrl(event.url);
  }).catch((error) => {
    logWarning(
      'deepLink.registerListener',
      'Could not register the native deep-link listener.',
      {
        feature: 'navigation',
        extra: {
          reason: error instanceof Error ? error.message : String(error),
        },
      }
    );
  });

  void App.getLaunchUrl()
    .then((launch) => {
      if (launch?.url) void navigateFromUrl(launch.url);
    })
    .catch((error) => {
      logWarning(
        'deepLink.getLaunchUrl',
        'Could not read the native launch URL.',
        {
          feature: 'navigation',
          extra: {
            reason: error instanceof Error ? error.message : String(error),
          },
        }
      );
    });
}
